import type { CommandEnvelope } from './commands';

/**
 * Outbox: the client-side queue every POS write goes through (Phase 0 section J.2). Phase 3 ships
 * the interface and an in-memory implementation: commands flush immediately while online, wait in
 * order while offline, and are never lost within the page's lifetime. Persistence across reloads
 * and replay rules arrive in Phase 13 without changing callers.
 *
 * Correctness rests on the server: a command is idempotent by its id, so re-sending after a
 * dropped connection cannot apply twice.
 *
 * ADR-024 (Phase 7A): a queued command belongs to the employee whose PIN session queued it and is
 * only ever sent while that employee is signed in on this tablet, never as the next person. While
 * they are not, their commands are parked and everyone else's keep flowing (except commands on an
 * order that has a parked command, to keep each order's commands in order). A queued payment the
 * server refuses is not dropped: it stays as a failed entry ("payment not recorded") until it is
 * sent again or discarded on the server.
 */
export interface OutboxStatus {
  /** Commands not yet delivered, parked and held ones included. */
  pending: number;
  /** Of those, waiting for an employee who is not signed in here. */
  parked: number;
  /** Of those, waiting behind a parked command on the same order (sent after it). */
  held: number;
  parkedByEmployee: readonly { employeeId: string; count: number }[];
  /** Queued payments the server refused, kept until sent again or discarded. */
  failed: number;
  flushing: boolean;
  online: boolean;
  lastError: string | null;
}

export interface OutboxEntry {
  envelope: CommandEnvelope;
  locationId?: string;
  /** The employee whose PIN session queued it (null: no employee session, e.g. a browser). */
  employeeId?: string | null;
  enqueuedAt: number;
  attempts: number;
  /** It was not delivered at once (offline, behind others, parked or a dropped connection). */
  delayed: boolean;
}

export interface FailedEntry {
  entry: OutboxEntry;
  error: unknown;
  refusedAt: number;
}

export interface OutboxTransport<R = unknown> {
  send(envelope: CommandEnvelope, locationId?: string): Promise<R>;
  /** True for connectivity failures (retry later), false for server rejections (fail now). */
  isNetworkError(error: unknown): boolean;
  /** True when the employee's session is gone or expired: park their commands, do not fail them. */
  isSessionError?(error: unknown): boolean;
}

export interface OutboxOptions {
  isOnline?: () => boolean;
  /** The employee signed in on this tablet now (null when nobody is). */
  currentEmployee?: () => string | null;
  /** A refused command that is kept as failed instead of dropped (queued payments). */
  keepOnRefusal?: (entry: OutboxEntry) => boolean;
}

export interface Outbox<R = unknown> {
  /** Resolves with the server result once the command has been applied, however long that takes. */
  enqueue(
    envelope: CommandEnvelope,
    options?: { locationId?: string; employeeId?: string | null },
  ): Promise<R>;
  /** Try to deliver everything that can be delivered now, in order. Safe to call any time. */
  flush(): Promise<void>;
  status(): OutboxStatus;
  subscribe(listener: (status: OutboxStatus) => void): () => void;
  /** Pending entries, oldest first (read-only view for UI and tests). */
  peek(): readonly OutboxEntry[];
  /** Refused entries kept for the employee to send again or discard. */
  failed(): readonly FailedEntry[];
  /** Put a failed entry back in the queue as it was (same command id). */
  retry(commandId: string): Promise<R>;
  /** Forget a failed entry (after the server recorded its discard, or it was recorded anew). */
  drop(commandId: string): void;
  /** True when the queue, failed entries aside, holds nothing for this employee. */
  isEmptyFor(employeeId: string | null): boolean;
  /** Commands of this employee that are being sent now (neither parked nor held). */
  sendingFor(employeeId: string | null): number;
  /** Commands that are not being sent: parked, and held behind a parked one on their order. */
  blocked(): readonly OutboxEntry[];
}

interface Waiting<R> extends OutboxEntry {
  resolve: (r: R) => void;
  reject: (e: unknown) => void;
}

/** The order a command acts on, if any (its commands are sent in order). */
export function commandOrderId(envelope: CommandEnvelope): string | null {
  const p = envelope.payload as { order_id?: unknown; id?: unknown };
  if (typeof p?.order_id === 'string') return p.order_id;
  if (envelope.kind === 'order.place' && typeof p?.id === 'string') return p.id;
  return null;
}
const orderOf = commandOrderId;

export function createMemoryOutbox<R = unknown>(
  transport: OutboxTransport<R>,
  options: OutboxOptions = {},
): Outbox<R> {
  const isOnline = options.isOnline ?? (() => true);
  const currentEmployee = options.currentEmployee ?? (() => null);
  const keepOnRefusal = options.keepOnRefusal ?? (() => false);
  const queue: Waiting<R>[] = [];
  const failedList: FailedEntry[] = [];
  const listeners = new Set<(s: OutboxStatus) => void>();
  /** Employees whose session the server refused during this flush: their commands wait. */
  let signedOut = new Set<string>();
  let flushing = false;
  let again = false;
  let lastError: string | null = null;

  const isParked = (e: OutboxEntry) =>
    !!e.employeeId && (e.employeeId !== currentEmployee() || signedOut.has(e.employeeId));
  /** Parked entries and, after one, every later entry on the same order (as flush holds them). */
  const blockedSet = (): Set<Waiting<R>> => {
    const blocked = new Set<Waiting<R>>();
    const orders = new Set<string>();
    for (const e of queue) {
      const order = orderOf(e.envelope);
      if (isParked(e) || (order && orders.has(order))) {
        blocked.add(e);
        if (order) orders.add(order);
      }
    }
    return blocked;
  };

  const status = (): OutboxStatus => {
    const parked = queue.filter(isParked);
    const blocked = blockedSet();
    const byEmployee = new Map<string, number>();
    for (const e of parked) byEmployee.set(e.employeeId!, (byEmployee.get(e.employeeId!) ?? 0) + 1);
    return {
      pending: queue.length,
      parked: parked.length,
      held: blocked.size - parked.length,
      parkedByEmployee: [...byEmployee].map(([employeeId, count]) => ({ employeeId, count })),
      failed: failedList.length,
      flushing,
      online: isOnline(),
      lastError,
    };
  };
  const notify = () => {
    const s = status();
    for (const l of listeners) l(s);
  };
  const view = ({
    envelope,
    locationId,
    employeeId,
    enqueuedAt,
    attempts,
    delayed,
  }: OutboxEntry): OutboxEntry => ({
    envelope,
    ...(locationId ? { locationId } : {}),
    employeeId: employeeId ?? null,
    enqueuedAt,
    attempts,
    delayed,
  });

  const flush = async (): Promise<void> => {
    if (flushing) {
      again = true;
      return;
    }
    flushing = true;
    signedOut = new Set();
    notify();
    try {
      do {
        again = false;
        // Orders with a command that has to wait: later commands on them wait too.
        const held = new Set<string>();
        let i = 0;
        while (i < queue.length) {
          if (!isOnline()) {
            lastError = 'offline';
            for (const e of queue) e.delayed = true;
            break;
          }
          const entry = queue[i]!;
          const order = orderOf(entry.envelope);
          if (isParked(entry) || (order && held.has(order))) {
            entry.delayed = true;
            if (order) held.add(order);
            i += 1;
            continue;
          }
          entry.attempts += 1;
          try {
            const result = await transport.send(entry.envelope, entry.locationId);
            queue.splice(queue.indexOf(entry), 1);
            lastError = null;
            entry.resolve(result);
          } catch (e) {
            if (transport.isNetworkError(e)) {
              // Keep it where it is; a later flush (reconnect) retries with the same command id.
              lastError = 'network';
              for (const x of queue) x.delayed = true;
              break;
            }
            if (transport.isSessionError?.(e) && entry.employeeId) {
              // Their session ended (idle lock, another sign-in): park their commands until they
              // sign in here again; everyone else's continue.
              signedOut.add(entry.employeeId);
              entry.delayed = true;
              if (order) held.add(order);
              lastError = 'session';
              i += 1;
              notify();
              continue;
            }
            // A server rejection is final for this command; the rest proceeds.
            queue.splice(queue.indexOf(entry), 1);
            lastError = e instanceof Error ? e.message : String(e);
            if (entry.delayed && keepOnRefusal(entry))
              failedList.push({ entry: view(entry), error: e, refusedAt: Date.now() });
            entry.reject(e);
          }
          notify();
        }
      } while (again && isOnline());
    } finally {
      flushing = false;
      notify();
    }
  };

  const enqueue: Outbox<R>['enqueue'] = (envelope, opts = {}) =>
    new Promise<R>((resolve, reject) => {
      queue.push({
        envelope,
        ...(opts.locationId ? { locationId: opts.locationId } : {}),
        employeeId: opts.employeeId ?? null,
        enqueuedAt: Date.now(),
        attempts: 0,
        delayed: false,
        resolve,
        reject,
      });
      notify();
      void flush();
    });

  return {
    enqueue,
    flush,
    status,
    subscribe(listener) {
      listeners.add(listener);
      listener(status());
      return () => listeners.delete(listener);
    },
    peek() {
      return queue.map(view);
    },
    failed() {
      return [...failedList];
    },
    retry(commandId) {
      const k = failedList.findIndex((f) => f.entry.envelope.command_id === commandId);
      if (k < 0) return Promise.reject(new Error('not a failed entry'));
      const [f] = failedList.splice(k, 1);
      notify();
      const p = new Promise<R>((resolve, reject) => {
        queue.push({ ...f!.entry, delayed: true, attempts: f!.entry.attempts, resolve, reject });
      });
      void flush();
      return p;
    },
    drop(commandId) {
      const k = failedList.findIndex((f) => f.entry.envelope.command_id === commandId);
      if (k >= 0) failedList.splice(k, 1);
      notify();
    },
    isEmptyFor(employeeId) {
      return !queue.some((e) => (e.employeeId ?? null) === employeeId);
    },
    sendingFor(employeeId) {
      const blocked = blockedSet();
      return queue.filter((e) => (e.employeeId ?? null) === employeeId && !blocked.has(e)).length;
    },
    blocked() {
      const blocked = blockedSet();
      return queue.filter((e) => blocked.has(e)).map(view);
    },
  };
}
